/**
 * Expandable detail for one completed dialogue — fetches the per-objective
 * breakdown (score, feedback, misconceptions) on demand. Reused by the learner's
 * My Progress and the admin/instructor learner view (different endpoints).
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";
import type { SessionDetail } from "../types";

export function DialogDetail({ url }: { url: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dialog-detail", url],
    queryFn: () => apiGet<SessionDetail>(url),
  });

  if (isLoading) return <p className="text-muted" style={{ padding: "8px 0" }}>Loading details…</p>;
  if (error || !data) return <p className="error-message">Couldn't load the breakdown.</p>;

  return (
    <div className="dialog-detail">
      {data.summary_text && <p className="dialog-detail-summary">{data.summary_text}</p>}
      {data.objective_scores.length === 0 ? (
        <p className="text-muted">No per-objective breakdown recorded.</p>
      ) : (
        <table>
          <thead><tr><th>Objective</th><th>Score</th><th>Feedback</th></tr></thead>
          <tbody>
            {data.objective_scores.map((o, i) => (
              <tr key={i}>
                <td>{o.objective}</td>
                <td>
                  <span className={o.score >= 70 ? "text-success" : o.score >= 50 ? "text-warning" : "text-danger"}>
                    {o.score}%
                  </span>
                </td>
                <td>
                  {o.feedback && <div>{o.feedback}</div>}
                  {o.misconceptions.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <span className="text-muted">Misconceptions: </span>
                      {o.misconceptions.map((m, j) => (
                        <span key={j} className="badge badge-warning">{m}</span>
                      ))}
                    </div>
                  )}
                  {o.calculation_errors.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <span className="text-muted">Arithmetic to double-check: </span>
                      {o.calculation_errors.map((c, j) => (
                        <span key={j} className="badge badge-primary">{c}</span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
